/**
 * Small presentational cells shared by the Access observability tables
 * (design 19 §7.1/§7.2, VSNP-1219).
 *
 * Only components live in this file — the repo's fast-refresh lint rule rejects
 * a module that mixes components with exported helpers.
 *
 * The recurring decision here is **absent vs zero**. A gateway that has not
 * reported CPU and a gateway reporting 0 % must not look the same: the first is
 * "we do not know", the second is "it is idle". Every cell renders `null` as a
 * muted em dash and only draws a bar when a number actually arrived.
 */

import { NODE_IFACE_FILTER_LABEL } from '../../utils/obsChart';

export const PercentCell = ({ value, warn = 80, danger = 90 }: { value: number | null; warn?: number; danger?: number }) => {
  if (value === null || !Number.isFinite(value)) return <span className="text-gray-300">—</span>;
  const pct = Math.max(0, Math.min(100, value));
  const tone = pct >= danger ? 'bg-danger-500' : pct >= warn ? 'bg-warning-500' : 'bg-success-500';
  return (
    <div className="min-w-[64px]">
      <div className="text-xs font-medium text-gray-700">{pct.toFixed(pct >= 10 ? 0 : 1)}%</div>
      <div className="mt-0.5 h-1.5 w-full rounded-full bg-gray-100">
        <div className={`h-1.5 rounded-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const NumberCell = ({ value, digits = 0, suffix = '' }: { value: number | null; digits?: number; suffix?: string }) => {
  if (value === null || !Number.isFinite(value)) return <span className="text-gray-300">—</span>;
  return (
    <span className="text-sm text-gray-800">
      {value.toFixed(digits)}
      {suffix && <span className="ml-0.5 text-xs text-gray-400">{suffix}</span>}
    </span>
  );
};

/** rx/tx on one line — they are read as a pair, and two columns doubles the width. */
export const ThroughputCell = ({ rx, tx }: { rx: number | null; tx: number | null }) => {
  if (rx === null && tx === null) return <span className="text-gray-300">—</span>;
  const fmt = (v: number | null) => (v === null ? '—' : v >= 10 ? v.toFixed(0) : v.toFixed(2));
  return (
    <span className="whitespace-nowrap text-xs text-gray-700" title={`Mbps, 5 min rate, ${NODE_IFACE_FILTER_LABEL}`}>
      <span className="text-gray-400">↓</span> {fmt(rx)} <span className="text-gray-400">↑</span> {fmt(tx)}
      <span className="ml-1 text-gray-400">Mbps</span>
    </span>
  );
};

/**
 * Policy bundle epoch, with a drift badge when the node is behind the
 * orchestrator's compiled epoch (design §7.1, alert `AccessBundleStale`).
 *
 * The two numbers come from different parties — the node reports
 * `agw_policy_bundle_epoch`, the orchestrator knows what it compiled — which is
 * the whole point: a node that agrees with itself proves nothing.
 */
export const BundleEpochCell = ({
  nodeEpoch,
  compiledEpoch,
  tenant,
}: {
  nodeEpoch: number | null;
  compiledEpoch: number | null;
  /** Which tenant the comparison is for, when the page is not scoped to one. */
  tenant?: string | null;
}) => {
  if (nodeEpoch === null) return <span className="text-gray-300">—</span>;
  const drift = compiledEpoch !== null && nodeEpoch < compiledEpoch;
  const whose = tenant ? `tenant ${tenant}'s` : "the tenant's";
  return (
    <span className="whitespace-nowrap">
      <span className="font-mono text-xs text-gray-700">{nodeEpoch}</span>
      {drift && (
        <span
          className="ml-1 rounded bg-warning-100 px-1 py-0.5 text-[10px] font-medium text-warning-700"
          title={`The gateway is on epoch ${nodeEpoch} for ${whose} bundle; ${whose} compiled policy is epoch ${compiledEpoch}`}
          data-testid="bundle-drift"
        >
          stale
        </span>
      )}
    </span>
  );
};

/**
 * A gateway's EFFECTIVE log level, with a "local override" badge when it
 * differs from the orchestrator's intent (VSNP-1338) — the log-level twin of
 * the bundle-drift badge above.
 *
 * The level shown is what the NODE reports its logger is running at, never the
 * intent column: on 2026-09-22 both dev-1 gateways ran at debug from a local
 * systemd drop-in while the intent was unset, and a console that showed the
 * intent could not have told anyone. The comparison itself is the API's
 * (`log_level_override`), so the list and the detail page cannot disagree
 * about what counts as an override. An unset intent means the node default,
 * info.
 */
export const LogLevelCell = ({
  effective,
  intent,
  override,
  since,
}: {
  effective: string | null | undefined;
  intent: string | null | undefined;
  override: boolean | null | undefined;
  since?: string | null;
}) => {
  if (!effective) {
    return (
      <span className="text-gray-300" title="This gateway's build does not report its log level">
        —
      </span>
    );
  }
  const intentLabel = intent ? intent : 'unset (node default: info)';
  const sinceLabel = since ? ` since ${new Date(since).toLocaleString()}` : '';
  return (
    <span className="whitespace-nowrap" data-testid="log-level-effective">
      <span
        className={`font-mono text-xs ${effective === 'debug' ? 'text-warning-700' : 'text-gray-700'}`}
        title={`Running at ${effective}${sinceLabel}. Orchestrator intent: ${intentLabel}.`}
      >
        {effective}
      </span>
      {override && (
        <span
          className="ml-1 rounded bg-warning-100 px-1 py-0.5 text-[10px] font-medium text-warning-700"
          title={`The gateway is running at ${effective}, but the orchestrator's log level is ${intentLabel}. Something on the node set it — typically LOG_LEVEL in a systemd drop-in under /etc/systemd/system/securelink-agw.service.d/. Setting the log level from the orchestrator moves the node back.`}
          data-testid="log-level-override"
        >
          local override
        </span>
      )}
    </span>
  );
};

/** Deny rate: stored per second by the query, read per minute by people. */
export const DenyRateCell = ({ perSecond }: { perSecond: number | null }) => {
  if (perSecond === null || !Number.isFinite(perSecond)) return <span className="text-gray-300">—</span>;
  const perMin = perSecond * 60;
  const tone = perMin >= 1 ? 'text-danger-600 font-medium' : perMin > 0 ? 'text-warning-600' : 'text-gray-500';
  return (
    <span className={`whitespace-nowrap text-xs ${tone}`} title="sum by (node) (rate(agw_flow_decisions_total{action=&quot;deny&quot;}[1h]))">
      {perMin >= 0.01 ? perMin.toFixed(2) : perMin === 0 ? '0' : perMin.toExponential(1)}
      <span className="ml-0.5 text-gray-400">/min</span>
    </span>
  );
};

/**
 * The User cell for anything backed by a session (brief §3 WP4 identity rule).
 *
 * A device-only session carries the certificate CN in `user` because that is
 * what the policy engine matched. Rendering that CN under "User" would claim a
 * person authenticated when nobody did, so it becomes "device-only" and the CN
 * shows in the Device column instead.
 */
export const UserCell = ({
  user,
  idpVerified,
  userEmail,
  userSub,
  preAttribution = false,
}: {
  user: string | null;
  idpVerified: boolean;
  userEmail?: string | null;
  userSub?: string | null;
  /**
   * The row is shown under a USER filter but carries no verified subject: it
   * was recorded before the gateway reported one (VSNP-1339) and matched the
   * filter by its recorded username or email only (VSNP-1437). Rendering it as
   * "device-only" next to an active "user: alice" chip contradicted the
   * filter; rendering it like a verified row would overstate what is known.
   * So it says exactly what it is.
   */
  preAttribution?: boolean;
}) => {
  if (preAttribution) {
    return (
      <span
        className="text-sm text-gray-600"
        title={
          'Recorded before identity attribution (VSNP-1339): the gateway did not yet report the verified ' +
          'IdP subject, so this session matches the filter by the username or email recorded on it, not by a ' +
          `verified identity.${idpVerified ? '' : ' It was also not marked IdP-verified when recorded.'}`
        }
        data-testid="user-pre-attribution"
      >
        {user ?? userEmail ?? '—'}
        <span className="block text-[10px] font-medium uppercase tracking-wide text-warning-700">
          recorded before identity attribution
        </span>
      </span>
    );
  }
  if (idpVerified && user) {
    // The username is what policy matched, so it leads. The address is shown
    // beneath it when it says something the username does not — which is how
    // an admin who arrived from a user page (keyed by email) recognises the
    // person on a session opened on somebody else's device (VSNP-1339). The
    // sub, the one identifier that never changes, is in the tooltip.
    const showEmail = !!userEmail && userEmail.toLowerCase() !== user.toLowerCase();
    const title = [userEmail && `email: ${userEmail}`, userSub && `sub: ${userSub}`].filter(Boolean).join('\n');
    return (
      <span className="text-sm text-gray-900" title={title || undefined} data-testid="user-verified">
        {user}
        {showEmail && <span className="block text-xs text-gray-500">{userEmail}</span>}
      </span>
    );
  }
  return (
    <span
      className="text-sm italic text-gray-400"
      title="no IdP identity — matched by device certificate"
      data-testid="user-device-only"
    >
      device-only
    </span>
  );
};
